import { Calendar1Icon, ContactRound, ClockIcon, CoffeeIcon, LogOutIcon, MapPin, SquarePen, ClockPlus, ChevronDownIcon, Search, FileSpreadsheet, FileText, User } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Label } from "@/components/ui/label"
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet"
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Table, TableBody, TableCaption, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import type { CombinedUser } from "@/lib/collections/workerProfile-Users/getUserWithProfile";
import { useAuth } from "@/context/AuthContext";
import { updateWorkBreak, updateWorkSession, createCompletedBreak } from "@/lib/collections/datapicker/updateSessionsAndBreaks";
import { toast } from "react-toastify";
import MapboxMap, { parseCoords } from "@/ubication/mapbox";
import { useMonthlyReportExcel } from "./reportExcel";
import { getMonthlyWorkSessionsDetailed, type WorkSessionDetailed } from "@/lib/collections/datapicker/reports-assitance/fetchReport";
import { pdf } from "@react-pdf/renderer";
import { MonthlyReportPDF } from "./reportPdf";
import NotesDialog from "./notesDialog";


// Definir tipo para los eventos en el componente
type SessionEvent = {
    tipo: string;
    id: string;
    sessionId?: string;
    label: string;
    hora: string;
    horaRaw: Date;
    lugar: string;
    startTime: string;
    endTime: string;
    startRaw?: Date;
    endRaw?: Date;
};

//para obtener el icono segun su tipo :P
const getIcon = (tipo: string, className: string = "w-4 h-4 text-black dark:text-white") => {
    switch (tipo) {
        case "clockIn":
            return <ClockPlus className={className} />;
        case "clockOut":
            return <LogOutIcon className={className} />;
        case "break":
            return <CoffeeIcon className={className} />;
        default:
            return <ClockIcon className={className} />;
    }
};

type DatepickerCollaboratorProps = {
    user: CombinedUser;
    sessionsWithBreaks: any[];
    onDateChange: (date: Date) => void;
    selectedDate: Date;
};

const DatePicker = ({ user, sessionsWithBreaks, onDateChange, selectedDate }: DatepickerCollaboratorProps) => {
    const { userRoles } = useAuth();
    const [date, _setDate] = useState<Date | undefined>(new Date());
    const selectedDateStr = selectedDate.toISOString().split('T')[0];
    const [eventosEditados, setEventosEditados] = useState<SessionEvent[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [newClockOutTime, setNewClockOutTime] = useState("");
    const [isAddingClockOut, setIsAddingClockOut] = useState(false);
    const [newBreakStart, setNewBreakStart] = useState("");
    const [newBreakEnd, setNewBreakEnd] = useState("");
    const [isAddingBreak, setIsAddingBreak] = useState(false);
    const [year, setYear] = useState<number>(new Date().getFullYear());
    const [month, setMonth] = useState<number>(new Date().getMonth() + 1);

    const [reportData, setReportData] = useState<WorkSessionDetailed[]>([]);
    const [loadingReport, setLoadingReport] = useState(false);

    const fetchReport = async () => {
        if (!user) return;
        setLoadingReport(true);
        try {
            const sessions = await getMonthlyWorkSessionsDetailed(
                user.id,
                year,
                month
            );
            setReportData(sessions);
        } catch (err) {
            console.error(err);
        } finally {
            setLoadingReport(false);
        }
    };


    // calcula horas totales para el reporte 
    const totalHours = reportData.reduce((sum, s) => sum + s.totalWorkTime, 0);


    const sessionOfTheDay = sessionsWithBreaks.find((session) => {
        const sessionDate = new Date(session.clockIn);
        const selectedDate = new Date(selectedDateStr + 'T00:00:00');

        return sessionDate.getFullYear() === selectedDate.getFullYear() &&
            sessionDate.getMonth() === selectedDate.getMonth() &&
            sessionDate.getDate() === selectedDate.getDate();
    });

    //darle formato a la hora de los input al editar en el sheet
    const formatToTimeInput = (dateString: string) => {
        const date = new Date(dateString);
        return date.toTimeString().split(" ")[0]; // devuelve "HH:mm:ss"
    };

    // Construir eventos iniciales con tipo explícito
    const eventos: SessionEvent[] = [];
    if (sessionOfTheDay) {
        // Evento de Clock In
        eventos.push({
            tipo: "clockIn",
            id: sessionOfTheDay.$id,
            label: "Clock In",
            hora: new Date(sessionOfTheDay.clockIn).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            horaRaw: new Date(sessionOfTheDay.clockIn),
            lugar: sessionOfTheDay.clockInLocation || "No especificado",
            startTime: formatToTimeInput(sessionOfTheDay.clockIn),
            endTime: ""
        });

        // Agregar breaks (si existen)
        if (sessionOfTheDay.breaks?.length) {
            sessionOfTheDay.breaks.forEach((b: any, idx: number) => {
                eventos.push({
                    tipo: "break",
                    id: b.$id,
                    sessionId: sessionOfTheDay.$id,
                    label: `Break ${idx + 1}`,
                    hora: `${new Date(b.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${b.endTime ? new Date(b.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "Activo"}`,
                    horaRaw: new Date(b.startTime),
                    startRaw: new Date(b.startTime),
                    endRaw: b.endTime ? new Date(b.endTime) : undefined,
                    lugar: b.location || "No especificado",
                    startTime: formatToTimeInput(b.startTime),
                    endTime: b.endTime ? formatToTimeInput(b.endTime) : "",
                });
            });
        }

        // vento de Clock Out (si existe)
        if (sessionOfTheDay.clockOut) {
            eventos.push({
                tipo: "clockOut",
                id: sessionOfTheDay.$id,
                label: "Clock Out",
                hora: new Date(sessionOfTheDay.clockOut).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                }),
                horaRaw: new Date(sessionOfTheDay.clockOut),
                lugar: sessionOfTheDay.clockOutLocation || "No especificado",
                startTime: formatToTimeInput(sessionOfTheDay.clockOut),
                endTime: ""
            });
        }
    }

    // inicializar eventos editados
    useEffect(() => {
        setEventosEditados([...eventos]);
    }, [sessionOfTheDay]);

    // manejar cambios en los inputs de tiempo
    const handleChangeHora = (index: number, campo: 'startTime' | 'endTime', valor: string) => {
        const nuevosEventos = [...eventosEditados];
        nuevosEventos[index] = {
            ...nuevosEventos[index],
            [campo]: valor
        };
        setEventosEditados(nuevosEventos);
    };

    // guardar cambios en la base de datos
    const handleSaveChanges = async () => {
        if (!sessionOfTheDay) return;
        setIsLoading(true);

        try {
            for (const evento of eventosEditados) {
                //funcion para crear fecha completa combinando fecha + hora
                const fullDate = (time: string) =>
                    time ? new Date(`${selectedDateStr}T${time}`).toISOString() : "";

                if (evento.tipo === "clockIn") {
                    await updateWorkSession(evento.id, {
                        clockIn: fullDate(evento.startTime)
                    });
                }
                else if (evento.tipo === "clockOut") {
                    await updateWorkSession(evento.id, {
                        clockOut: fullDate(evento.startTime)
                    });
                }
                else if (evento.tipo === "break") {
                    await updateWorkBreak(evento.id, {
                        startTime: fullDate(evento.startTime),
                        endTime: evento.endTime ? fullDate(evento.endTime) : null
                    });
                }
            }
            toast.success("Cambios guardados exitosamente!")
            // recargar los datos o actualizar el estado
        } catch (error) {
            console.error("Error al guardar cambios:", error);
            toast.error("Hubo un error al guardar los cambios");
        } finally {
            setIsLoading(false);
        }
    };

    const buildFullDate = (time: string) =>
        time ? new Date(`${selectedDateStr}T${time}`).toISOString() : "";

    const handleAddClockOut = async () => {
        if (!sessionOfTheDay || !newClockOutTime) return;
        setIsAddingClockOut(true);
        try {
            await updateWorkSession(sessionOfTheDay.$id, { clockOut: buildFullDate(newClockOutTime) });
            toast.success("Clock out registrado correctamente.");
            setNewClockOutTime("");
        } catch {
            toast.error("Error al registrar el clock out.");
        } finally {
            setIsAddingClockOut(false);
        }
    };

    const handleAddBreak = async () => {
        if (!sessionOfTheDay || !newBreakStart || !newBreakEnd) return;
        const startISO = buildFullDate(newBreakStart);
        const endISO = buildFullDate(newBreakEnd);
        if (new Date(endISO) <= new Date(startISO)) {
            toast.error("La hora final debe ser mayor que la inicial.");
            return;
        }
        setIsAddingBreak(true);
        try {
            const duration = (new Date(endISO).getTime() - new Date(startISO).getTime()) / 60000;
            await createCompletedBreak(
                sessionOfTheDay.$id,
                sessionOfTheDay.userId as string,
                startISO,
                endISO,
                duration
            );
            toast.success("Break registrado correctamente.");
            setNewBreakStart("");
            setNewBreakEnd("");
        } catch {
            toast.error("Error al registrar el break.");
        } finally {
            setIsAddingBreak(false);
        }
    };

    if (!user) {
        return <div className="text-center p-8">Usuario no encontrado</div>;
    }

    //handler report para activar el filtrado del reporte en la tabla 
    const onSearch = (e: React.FormEvent) => {
        e.preventDefault();
        fetchReport();
    };

    const generateExcel = useMonthlyReportExcel(reportData, totalHours, year, month, user.name);

    const fileName = `reporte_${user.name}_${String(month).padStart(2, '0')}_${year}.pdf`;

    const handleDownloadPDF = async () => {
        if (reportData.length === 0) return;

        // 1) Creamos el documento PDF en memoria
        const blob = await pdf(
            <MonthlyReportPDF
                reportData={reportData}
                totalHours={totalHours}
                year={year}
                month={month}
                userName={user.name}
            />
        ).toBlob();


        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };
    return (

        <>
            <Breadcrumb>
                <BreadcrumbList>
                    <BreadcrumbItem>
                        <div className="flex items-center gap-1.5">
                            <ContactRound />
                            <BreadcrumbPage>Asistencia</BreadcrumbPage>
                        </div>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                            <Link to="/assistance">Colaboradores</Link>
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbPage className="hidden sm:block">{user.name}</BreadcrumbPage>
                        <BreadcrumbPage className="block sm:hidden "> <User className="w-5 h-5" /> </BreadcrumbPage>
                    </BreadcrumbItem>
                </BreadcrumbList>
            </Breadcrumb>

            <div className="py-6 pb-24">
                <Card className="w-full max-w-full overflow-hidden shadow-lg">
                    <CardContent className="p-4">
                        <div className="mt-2 flex flex-col lg:flex-row gap-6 items-start">
                            {/* parte izquierda de la card */}
                            <div className="w-full lg:w-2/5 flex flex-col items-center lg:items-start gap-6">
                                <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 w-full">
                                    <img
                                        src={user.avatarUrl}
                                        className="w-17 h-17 rounded-full object-cover shrink-0"
                                        alt=""
                                    />
                                    <div className="text-center sm:text-left w-full wrap-break-word">
                                        <h1 className="text-base font-medium font-outfit">{user.name}</h1>
                                        {user.profile ? (
                                            <p className="text-gray-500 text-xs font-nunito wrap-break-word">
                                                {user.profile.rolCorp} <span>-{user.profile.colabCorpCode}</span>
                                            </p>
                                        ) : (
                                            <p className="text-gray-400 text-xs italic">No asignado</p>
                                        )}

                                    </div>
                                </div>
                                <div>
                                    <div className="lg:ml-20">
                                        {/* Calendario */}
                                        <Calendar
                                            mode="single"
                                            selected={selectedDate}
                                            onSelect={(date) => {
                                                if (date) {
                                                    onDateChange(date);
                                                }
                                            }}
                                            className="rounded-md border shadow-sm text-gray-400"
                                            captionLayout="dropdown"
                                        />


                                        {/* Dialog para la generación de reportes */}
                                        <Dialog>
                                            {/* El trigger no va dentro del form */}
                                            <DialogTrigger asChild>
                                                <Button variant="default" className="mt-4 w-62 justify-center cursor-pointer">
                                                    <Calendar1Icon /> Reporte Mensual
                                                </Button>
                                            </DialogTrigger>

                                            <DialogContent
                                                className="w-full mx-auto px-2 sm:px-4 max-w-sm sm:max-w-md lg:max-w-206.25 "
                                            >
                                                <DialogHeader>
                                                    <DialogTitle className="text-center font-outfit text-xl">
                                                        Generar Reporte
                                                    </DialogTitle>
                                                    <DialogDescription>
                                                        Selecciona el mes y año para generar el reporte.
                                                    </DialogDescription>
                                                </DialogHeader>

                                                {/* Formulario de filtros */}
                                                <form onSubmit={onSearch} className="mt-2 flex gap-2 items-end">
                                                    {/* Año */}
                                                    <div>
                                                        <Label>Año</Label>
                                                        <Select value={year.toString()}
                                                            onValueChange={val => setYear(Number(val))}>
                                                            <SelectTrigger className="w-20 cursor-pointer">
                                                                <SelectValue placeholder="—" />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                <SelectGroup>
                                                                    <SelectLabel>Año</SelectLabel>
                                                                    {years.map(y => (
                                                                        <SelectItem key={y} value={y.toString()} className="cursor-pointer">
                                                                            {y}
                                                                        </SelectItem>
                                                                    ))}
                                                                </SelectGroup>
                                                            </SelectContent>
                                                        </Select>
                                                    </div>

                                                    {/* Mes */}
                                                    <div>
                                                        <Label>Mes</Label>
                                                        <Select value={(month - 1).toString()} onValueChange={val => setMonth(Number(val) + 1)}>
                                                            <SelectTrigger className="w-20 cursor-pointer">
                                                                <SelectValue placeholder="—" />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                <SelectGroup>
                                                                    <SelectLabel>Mes</SelectLabel>
                                                                    {months.map((m, idx) => (
                                                                        <SelectItem key={idx} value={idx.toString()} className="cursor-pointer">
                                                                            {m}
                                                                        </SelectItem>
                                                                    ))}
                                                                </SelectGroup>
                                                            </SelectContent>
                                                        </Select>
                                                    </div>

                                                    {/* Botón Buscar */}
                                                    <Button
                                                        type="submit"
                                                        disabled={loadingReport || year == null || month == null}
                                                        className="cursor-pointer"
                                                    >
                                                        <Search />
                                                    </Button>

                                                    {/* botones  descargar reporte */}
                                                    <div className="hidden sm:block ml-auto" />
                                                    <div className=" sm:block ml-auto" />

                                                    <Button
                                                        type="button"
                                                        onClick={generateExcel}
                                                        disabled={reportData.length === 0}
                                                        className="bg-green-500 hover:bg-green-600 flex items-center gap-1 cursor-pointer"
                                                    >
                                                        <FileSpreadsheet className="hidden lg:block" /> <span className="hidden lg:block">Descargar</span>Excel
                                                    </Button>

                                                    <Button
                                                        onClick={handleDownloadPDF}
                                                        disabled={reportData.length === 0}
                                                        className="bg-red-500 hover:bg-red-600 flex items-center gap-1 cursor-pointer"
                                                    >
                                                        <FileText className="hidden lg:block" />
                                                        <span className="hidden lg:block">Descargar</span>PDF
                                                    </Button>


                                                </form>

                                                {/* Resultados */}
                                                {year != null && month != null && (
                                                    <div className="mt-4">
                                                        {loadingReport ? (
                                                            <p>Cargando...</p>
                                                        ) : reportData.length > 0 ? (
                                                            <Card className="w-full py-2 px-2 sm:px-4 font-outfit">
                                                                <div className="overflow-auto max-h-[60vh]">
                                                                    <Table className="min-w-full">
                                                                        <TableCaption>Reporte de Asistencia</TableCaption>
                                                                        <TableHeader>
                                                                            <TableRow>
                                                                                <TableHead>#</TableHead>
                                                                                <TableHead className="">Fecha</TableHead>
                                                                                <TableHead>Clock In</TableHead>
                                                                                <TableHead>Clock Out</TableHead>
                                                                                <TableHead className="text-right">Horas</TableHead>
                                                                            </TableRow>
                                                                        </TableHeader>
                                                                        <TableBody className="text-gray-500">
                                                                            {reportData.map((session, idx) => (
                                                                                <TableRow key={session.$id}>
                                                                                    <TableCell>{idx + 1}</TableCell>
                                                                                    <TableCell>
                                                                                        {new Date(session.clockIn).toLocaleDateString("es-ES", {
                                                                                            day: "2-digit",
                                                                                            month: "2-digit",
                                                                                            year: "numeric",
                                                                                        })}
                                                                                    </TableCell>
                                                                                    <TableCell>
                                                                                        {new Date(session.clockIn).toLocaleTimeString([], {
                                                                                            hour: "2-digit",
                                                                                            minute: "2-digit",
                                                                                        })}
                                                                                    </TableCell>
                                                                                    <TableCell>
                                                                                        {session.clockOut
                                                                                            ? new Date(session.clockOut).toLocaleTimeString([], {
                                                                                                hour: "2-digit",
                                                                                                minute: "2-digit",
                                                                                            })
                                                                                            : "S/H"}
                                                                                    </TableCell>
                                                                                    <TableCell className="text-right">
                                                                                        {session.totalWorkTime.toFixed(2)}
                                                                                    </TableCell>
                                                                                </TableRow>
                                                                            ))}
                                                                        </TableBody>
                                                                        <TableFooter>
                                                                            <TableRow>
                                                                                <TableCell colSpan={4}>Total horas mes</TableCell>
                                                                                <TableCell className="text-right">
                                                                                    {totalHours.toFixed(2)}
                                                                                </TableCell>
                                                                            </TableRow>
                                                                        </TableFooter>
                                                                    </Table>
                                                                </div>
                                                            </Card>

                                                        ) : (
                                                            <p className="text-center text-gray-500">
                                                                No se han encontrado registros
                                                            </p>
                                                        )}
                                                    </div>
                                                )}

                                                <DialogFooter className="mt-4 flex justify-between">

                                                    <DialogClose asChild>
                                                        <Button variant="default" className="cursor-pointer">Cancelar</Button>
                                                    </DialogClose>
                                                </DialogFooter>
                                            </DialogContent>
                                        </Dialog>

                                    </div>
                                </div>
                            </div>
                            {/* el road map de la jornada(cotando breaks ) */}
                            <div className="w-full max-w-full lg:w-3/5 font-outfit border rounded-lg overflow-auto h-[60vh] box-border">
                                {/* Sheet para modificar las horas de la jornada y breaks  */}
                                <div className="flex justify-end gap-2 w-full mb-4 px-4 mt-4">
                                    {/* Botón de Notas - solo mostrar si hay notas para la sesión */}
                                    {sessionOfTheDay && (
                                        <NotesDialog
                                            sessionId={sessionOfTheDay.$id}
                                            userName={user.name}
                                            selectedDate={selectedDate}
                                        />
                                    )}

                                    <Sheet >
                                        <SheetTrigger asChild>
                                            <Button variant="outline" className="cursor-pointer">Editar<SquarePen /></Button>
                                        </SheetTrigger>
                                        <SheetContent side="right" className="overflow-y-auto max-h-screen py-2 overflow-x-auto">
                                            <SheetHeader>
                                                <SheetTitle className="text-2xl">Editar Sesión</SheetTitle>
                                                <SheetDescription className="text-xs">
                                                    {user.name}
                                                </SheetDescription>
                                                <Separator />
                                                <div className="grid cols-3 auto-rows-min gap-6 text-black dark:text-white overflow-hidden font-nunito">
                                                    <div className="grid grid-col-2 gap-2">
                                                        <div className="space-y-10">

                                                            {eventosEditados.map((evento, idx) => (
                                                                <div key={idx} className="flex items-start gap-4 relative flex-wrap">
                                                                    {/* Ícono */}
                                                                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-black dark:bg-white">
                                                                        {getIcon(evento.tipo, "w-5 h-5 text-white dark:text-black")}
                                                                    </div>
                                                                    <Label className="text-lg">{evento.label}</Label>

                                                                    <Label className="text-base text-gray-400 items-center">(<span> {selectedDate.toLocaleDateString('es-ES', {
                                                                        day: 'numeric',
                                                                        month: 'long',
                                                                        year: 'numeric'
                                                                    })}</span>)</Label>

                                                                    {/* Inputs dinámicos */}
                                                                    <div className="flex flex-col-1 gap-1">
                                                                        <div className="flex gap-2">
                                                                            {/* Fecha deshabilitada */}
                                                                            <div className=" flex-col gap-3 hidden">
                                                                                <Label className="px-1 text-gray-500">
                                                                                    Fecha:
                                                                                </Label>
                                                                                <Button
                                                                                    variant="outline"
                                                                                    disabled
                                                                                    className="w-25 justify-between font-normal"
                                                                                >
                                                                                    {date?.toLocaleDateString() || "Select date"}
                                                                                    <ChevronDownIcon />
                                                                                </Button>
                                                                            </div>

                                                                            {/* Inputs según tipo de evento */}
                                                                            {evento.tipo === "break" ? (
                                                                                <>
                                                                                    <div className="flex flex-col gap-3">
                                                                                        <Label className="px-1 text-gray-500">
                                                                                            Hora inicial:
                                                                                        </Label>
                                                                                        <Input
                                                                                            type="time"
                                                                                            step="1"
                                                                                            value={evento.startTime}
                                                                                            onChange={(e) => handleChangeHora(idx, 'startTime', e.target.value)}
                                                                                            className="bg-background [&::-webkit-calendar-picker-indicator]:brightness-0 dark:[&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-60 [&::-webkit-calendar-picker-indicator]:hover:opacity-100 [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                                                                                        />
                                                                                    </div>
                                                                                    <div className="flex flex-col gap-3">
                                                                                        <Label className="px-1 text-gray-500">
                                                                                            Hora final:
                                                                                        </Label>
                                                                                        <Input
                                                                                            type="time"
                                                                                            step="1"
                                                                                            value={evento.endTime}
                                                                                            onChange={(e) => handleChangeHora(idx, 'endTime', e.target.value)}
                                                                                            className="bg-background [&::-webkit-calendar-picker-indicator]:brightness-0 dark:[&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-60 [&::-webkit-calendar-picker-indicator]:hover:opacity-100 [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                                                                                        />
                                                                                    </div>
                                                                                </>
                                                                            ) : (
                                                                                <div className="flex flex-col gap-3">
                                                                                    <Label className="px-1 text-gray-500">
                                                                                        Hora:
                                                                                    </Label>
                                                                                    <Input
                                                                                        type="time"
                                                                                        step="1"
                                                                                        value={evento.startTime}
                                                                                        onChange={(e) => handleChangeHora(idx, 'startTime', e.target.value)}
                                                                                        className="bg-background [&::-webkit-calendar-picker-indicator]:brightness-0 dark:[&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-60 [&::-webkit-calendar-picker-indicator]:hover:opacity-100 [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                                                                                    />
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            {/* Sección: Agregar eventos — solo admin */}
                                            {sessionOfTheDay && userRoles.isAdmin && (
                                                <div className="mt-6 space-y-6 text-black dark:text-white font-nunito">
                                                    <Separator />

                                                    {/* Agregar Clock Out — solo si no existe */}
                                                    {!sessionOfTheDay.clockOut && (
                                                        <div className="space-y-3">
                                                            <p className="text-sm font-semibold flex items-center gap-2">
                                                                <LogOutIcon className="w-4 h-4" /> Agregar Clock Out
                                                            </p>
                                                            <div className="flex items-end gap-2">
                                                                <div className="flex flex-col gap-1">
                                                                    <Label className="text-xs text-gray-500">Hora</Label>
                                                                    <Input
                                                                        type="time"
                                                                        step="1"
                                                                        value={newClockOutTime}
                                                                        onChange={e => setNewClockOutTime(e.target.value)}
                                                                        className="bg-background [&::-webkit-calendar-picker-indicator]:brightness-0 dark:[&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-60 [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                                                                    />
                                                                </div>
                                                                <Button
                                                                    size="sm"
                                                                    onClick={handleAddClockOut}
                                                                    disabled={!newClockOutTime || isAddingClockOut}
                                                                    className="cursor-pointer"
                                                                >
                                                                    {isAddingClockOut ? "Guardando..." : "Guardar"}
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Agregar Break */}
                                                    <div className="space-y-3">
                                                        <p className="text-sm font-semibold flex items-center gap-2">
                                                            <CoffeeIcon className="w-4 h-4" /> Agregar Break
                                                        </p>
                                                        <div className="flex flex-wrap items-end gap-2">
                                                            <div className="flex flex-col gap-1">
                                                                <Label className="text-xs text-gray-500">Hora inicial</Label>
                                                                <Input
                                                                    type="time"
                                                                    step="1"
                                                                    value={newBreakStart}
                                                                    onChange={e => setNewBreakStart(e.target.value)}
                                                                    className="bg-background [&::-webkit-calendar-picker-indicator]:brightness-0 dark:[&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-60 [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                                                                />
                                                            </div>
                                                            <div className="flex flex-col gap-1">
                                                                <Label className="text-xs text-gray-500">Hora final</Label>
                                                                <Input
                                                                    type="time"
                                                                    step="1"
                                                                    value={newBreakEnd}
                                                                    onChange={e => setNewBreakEnd(e.target.value)}
                                                                    className="bg-background [&::-webkit-calendar-picker-indicator]:brightness-0 dark:[&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-60 [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                                                                />
                                                            </div>
                                                            <Button
                                                                size="sm"
                                                                onClick={handleAddBreak}
                                                                disabled={!newBreakStart || !newBreakEnd || isAddingBreak}
                                                                className="cursor-pointer"
                                                            >
                                                                {isAddingBreak ? "Guardando..." : "Guardar"}
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                            </SheetHeader>
                                            <SheetFooter>
                                                <Button
                                                    onClick={handleSaveChanges}
                                                    disabled={isLoading}
                                                    className="cursor-pointer"
                                                >
                                                    {isLoading ? "Guardando..." : "Guardar cambios"}
                                                </Button>
                                                <SheetClose asChild>
                                                    <Button variant="outline" className="cursor-pointer">Cancelar</Button>
                                                </SheetClose>
                                            </SheetFooter>
                                        </SheetContent>
                                    </Sheet>
                                </div>

                                {eventos.length === 0 && (
                                    <p className="text-muted-foreground">No hay sesiones para la fecha seleccionada.</p>
                                )}


                                {/* Roadmap de la jornada */}
                                <div className="relative pl-6">

                                    <div className="space-y-10">
                                        {eventos.map((evento, idx) => {
                                            // 1) Declaramos coords fuera del try
                                            let coords: [number, number];

                                            // 2) Intentamos parsear, o fallback si algo falla
                                            try {
                                                coords = parseCoords(evento.lugar);
                                            } catch (e) {
                                                console.warn("Error parseando coords:", e);
                                                coords = [0, 0];
                                            }

                                            return (
                                                <div key={idx} className="relative flex items-start gap-4">
                                                    {idx !== eventos.length - 1 && (
                                                        <div className="absolute left-4 h-full top-9 border-l-2 bg-black dark:bg-gray-500" />
                                                    )}
                                                    <div className="absolute left-0 top-0 w-9 h-9 flex items-center justify-center rounded-full bg-black dark:bg-gray-600 shadow">
                                                        {getIcon(evento.tipo, "w-4 h-4 text-white dark:text-white")}
                                                    </div>
                                                    <div className="ml-15">
                                                        <h2 className="text-base font-semibold text-foreground pt-1.5">{evento.label}</h2>
                                                        <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                                                            <ClockIcon className="w-4 h-4" />
                                                            <span className="text-gray-600 dark:text-gray-300">{evento.hora}</span>
                                                        </div>
                                                        <div className="flex items-center gap-2 text-sm text-primary mt-1">
                                                            <MapPin className="w-4 h-4 text-blue-500 dark:text-blue-400" />
                                                            {/* <span className="text-blue-500">{evento.lugar}</span> */}

                                                            <Dialog>

                                                                <DialogTrigger asChild>
                                                                    <button className="text-blue-500 dark:text-blue-400 cursor-pointer hover:underline">
                                                                        Ubicación
                                                                    </button>
                                                                </DialogTrigger>


                                                                <DialogContent className="w-full max-w-2xl">
                                                                    <DialogHeader>
                                                                        <DialogTitle>Ubicación de {evento.label}</DialogTitle>
                                                                        <DialogDescription>
                                                                            Cordenadas: <span className="text-blue-500"> {coords[1].toFixed(6)}, {coords[0].toFixed(6)}</span>                                                                         </DialogDescription>
                                                                    </DialogHeader>

                                                                    {/* Aquí renderizas tu Mapa */}
                                                                    <MapboxMap coords={coords} height="h-70" />

                                                                    <DialogClose asChild>
                                                                        <Button className="mt-4 px-4 py-2 cursor-pointer">Cerrar</Button>
                                                                    </DialogClose>
                                                                </DialogContent>
                                                            </Dialog>

                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div >
        </>
    );
};

//const de los meses para el select en reportes
const months = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
];

//const de los años para el select en reportes
const years = Array.from({ length: 31 }, (_, i) => 2000 + i); // años del 2000 a 2030

export default DatePicker;